import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import {
  Database, AlertTriangle, AlertCircle, CheckCircle2, Clock, ShieldAlert,
  ChevronLeft, RefreshCw, Calendar, BarChart3, HardDrive,
} from "lucide-react";

/**
 * Read-only diagnostic page for the kpi_entries collection.
 *
 * Backend: GET /api/admin/kpi-diagnostics (admin-only).
 *
 * This page exists to answer "did historical KPI data get deleted?" when
 * the staff dashboard suddenly looks empty. It runs zero writes — no
 * delete, no seed, no restore, no reset. The "Refresh" button re-runs the
 * same READ-only diagnostic; nothing else is mutable from here.
 */

const VERDICT_TONE = {
  healthy: {
    icon: CheckCircle2,
    cls: "border-sage/30 bg-sage/8 text-brand-primary",
    pill: "bg-sage/20 text-sage",
    label: "Data appears healthy",
  },
  hidden_or_truncated: {
    icon: AlertTriangle,
    cls: "border-gold/30 bg-gold/8 text-brand-primary",
    pill: "bg-gold/20 text-gold",
    label: "Data appears hidden or truncated",
  },
  missing: {
    icon: AlertCircle,
    cls: "border-terracotta/30 bg-terracotta/8 text-brand-primary",
    pill: "bg-terracotta/15 text-terracotta",
    label: "Data appears actually missing — recovery likely needed",
  },
};

export default function KPIDiagnostics() {
  const { user } = useAuth();
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/kpi-diagnostics");
      setReport(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load diagnostic.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (user && user.role !== "admin") {
    return (
      <div className="p-8 max-w-md mx-auto">
        <Card className="p-6 rounded-2xl border border-terracotta/30">
          <p className="text-brand-primary">This page is admin-only.</p>
        </Card>
      </div>
    );
  }

  const verdict = report?.verdict || "healthy";
  const tone = VERDICT_TONE[verdict] || VERDICT_TONE.healthy;
  const VerdictIcon = tone.icon;

  return (
    <div className="space-y-6 max-w-6xl" data-testid="kpi-diagnostics-page">
      {/* ── header ──────────────────────────────────────────────── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link to="/settings" className="text-xs text-terracotta hover:underline inline-flex items-center gap-1">
            <ChevronLeft className="h-3 w-3" /> Settings
          </Link>
          <h1 className="font-heading text-2xl sm:text-3xl text-brand-primary mt-1 flex items-center gap-2">
            <Database className="h-6 w-6 text-terracotta" strokeWidth={1.7} />
            KPI Diagnostics
          </h1>
          <p className="text-sm text-brand-primary/60 mt-1">
            Read-only inspection of the <span className="font-mono">kpi_entries</span> collection. No deletes, restores, or seed actions occur from this page.
          </p>
        </div>
        <Button
          onClick={load}
          disabled={loading}
          data-testid="kpi-diagnostics-refresh"
          variant="outline"
          className="rounded-full border-brand-primary/20"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
          {loading ? "Loading..." : "Refresh"}
        </Button>
      </div>

      {/* ── error ───────────────────────────────────────────────── */}
      {error && (
        <Card className="p-5 rounded-2xl border border-terracotta/30 bg-terracotta/5">
          <p className="text-terracotta font-medium">Failed to run diagnostic</p>
          <p className="text-sm text-brand-primary/70 mt-1">{error}</p>
        </Card>
      )}

      {/* ── verdict banner ─────────────────────────────────────── */}
      {report && (
        <Card
          data-testid={`kpi-diagnostics-verdict-${verdict}`}
          className={`p-6 rounded-2xl border ${tone.cls} shadow-warm`}
        >
          <div className="flex items-start gap-4">
            <VerdictIcon className="h-7 w-7 mt-0.5 shrink-0" strokeWidth={1.6} />
            <div className="flex-1 min-w-0">
              <Badge className={`rounded-full text-[10px] uppercase tracking-wider mb-2 ${tone.pill}`}>
                {tone.label}
              </Badge>
              <p className="text-base font-medium text-brand-primary">{report.summary}</p>
              <p className="text-xs text-brand-primary/55 mt-2 inline-flex items-center gap-1">
                <Clock className="h-3 w-3" /> Checked {new Date(report.checked_at).toLocaleString()}
              </p>
            </div>
          </div>
        </Card>
      )}

      {/* ── core counts ─────────────────────────────────────────── */}
      {report && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatTile label="Rows in DB"          value={report.total_db_count}    icon={Database} testId="stat-db-count" />
          <StatTile label="Rows from API"       value={report.api_real_count}    icon={BarChart3} testId="stat-api-count"
                    subline={report.api_matches_db ? "matches DB" : `mismatch vs DB (${report.total_db_count})`}
                    sublineTone={report.api_matches_db ? "ok" : "warn"} />
          <StatTile label="Derived rows (auto)" value={report.api_derived_count} icon={BarChart3} testId="stat-derived-count" subline="total_sunday_attendance synthesized" />
          <StatTile label="Distinct weeks"      value={report.overall?.distinct_weeks ?? 0} icon={Calendar} testId="stat-weeks-count"
                    subline={`${report.overall?.earliest_week || "—"} → ${report.overall?.latest_week || "—"}`} />
        </div>
      )}

      {/* ── legacy seeder dry-run ───────────────────────────────── */}
      {report && (
        <Card className="p-5 rounded-2xl bg-white border border-brand-primary/10 shadow-warm" data-testid="legacy-seeder-dryrun">
          <div className="flex items-start gap-3">
            <ShieldAlert className={`h-5 w-5 mt-0.5 ${report.legacy_seeder_dry_run.matched_count > 0 ? "text-terracotta" : "text-sage"}`} strokeWidth={1.7} />
            <div className="flex-1 min-w-0">
              <p className="font-medium text-brand-primary">Legacy seed-cleanup filter dry-run</p>
              <p className="text-[11px] text-brand-primary/55 uppercase tracking-wider mt-0.5">{report.legacy_seeder_dry_run.label}</p>
              <p className="text-sm text-brand-primary/80 mt-2">
                Matched rows under old code: <span className="font-mono font-semibold" data-testid="legacy-dryrun-count">{report.legacy_seeder_dry_run.matched_count}</span>
              </p>
              {report.legacy_seeder_dry_run.sample_ids?.length > 0 && (
                <details className="mt-3 text-xs">
                  <summary className="cursor-pointer text-brand-primary/55 hover:text-brand-primary">Sample matching IDs ({report.legacy_seeder_dry_run.sample_ids.length})</summary>
                  <ul className="mt-2 space-y-1 font-mono text-brand-primary/70">
                    {report.legacy_seeder_dry_run.sample_ids.map((s, i) => <li key={i}>· {s}</li>)}
                  </ul>
                </details>
              )}
              {report.legacy_seeder_dry_run.matched_count === 0 && (
                <p className="text-sm text-sage mt-2">No rows match — the legacy seed cleanup was never the cause of any deletion in this database.</p>
              )}
            </div>
          </div>
        </Card>
      )}

      {/* ── by category/source breakdown ────────────────────────── */}
      {report && (
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="kpi-diagnostics-by-cat-src">
          <div className="p-5 border-b border-brand-primary/10">
            <p className="font-heading text-lg text-brand-primary">Counts by category &amp; source</p>
            <p className="text-xs text-brand-primary/55 mt-0.5">Which categories have data and where it came from.</p>
          </div>
          <table className="min-w-full">
            <thead className="bg-cream text-[10px] uppercase tracking-wider text-brand-primary/55">
              <tr>
                <th className="text-left px-5 py-2.5">Category</th>
                <th className="text-left px-5 py-2.5">Source</th>
                <th className="text-right px-5 py-2.5">Count</th>
              </tr>
            </thead>
            <tbody>
              {report.by_category_source.map((r, i) => (
                <tr key={i} className="border-t border-brand-primary/5">
                  <td className="px-5 py-2.5 font-mono text-sm text-brand-primary">{r.category}</td>
                  <td className="px-5 py-2.5 text-sm text-brand-primary/75">
                    <Badge className="rounded-full bg-brand-primary/5 text-brand-primary/70 border-0">{r.source}</Badge>
                  </td>
                  <td className="px-5 py-2.5 text-right font-mono text-sm text-brand-primary">{r.count}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ── per-category details ────────────────────────────────── */}
      {report && (
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="kpi-diagnostics-per-cat">
          <div className="p-5 border-b border-brand-primary/10">
            <p className="font-heading text-lg text-brand-primary">Per-category date coverage</p>
            <p className="text-xs text-brand-primary/55 mt-0.5">Earliest week, latest week, distinct weeks, and any detected gaps.</p>
          </div>
          <table className="min-w-full">
            <thead className="bg-cream text-[10px] uppercase tracking-wider text-brand-primary/55">
              <tr>
                <th className="text-left  px-5 py-2.5">Category</th>
                <th className="text-right px-5 py-2.5">Rows</th>
                <th className="text-left  px-5 py-2.5">Earliest week</th>
                <th className="text-left  px-5 py-2.5">Latest week</th>
                <th className="text-right px-5 py-2.5">Distinct weeks</th>
                <th className="text-right px-5 py-2.5">Gaps detected</th>
              </tr>
            </thead>
            <tbody>
              {report.per_category.map((c) => (
                <tr key={c.category} className="border-t border-brand-primary/5">
                  <td className="px-5 py-2.5 font-mono text-sm text-brand-primary">{c.category}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-sm">{c.count}</td>
                  <td className="px-5 py-2.5 text-sm">{c.earliest_week || "—"}</td>
                  <td className="px-5 py-2.5 text-sm">{c.latest_week || "—"}</td>
                  <td className="px-5 py-2.5 text-right font-mono text-sm">{c.distinct_weeks}</td>
                  <td className="px-5 py-2.5 text-right text-sm">
                    {c.gap_weeks_total === 0 ? (
                      <span className="text-sage">0</span>
                    ) : (
                      <details>
                        <summary className={`cursor-pointer ${c.gap_weeks_total > 8 ? "text-terracotta font-semibold" : "text-gold"}`}>
                          {c.gap_weeks_total}
                        </summary>
                        <div className="mt-2 max-h-32 overflow-y-auto text-[11px] text-brand-primary/65 font-mono text-left">
                          {c.gap_weeks_sample.map((w, i) => <div key={i}>{w}</div>)}
                          {c.gap_weeks_total > c.gap_weeks_sample.length && (
                            <div className="text-brand-primary/40">…and {c.gap_weeks_total - c.gap_weeks_sample.length} more</div>
                          )}
                        </div>
                      </details>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* ── backup history ─────────────────────────────────────── */}
      {report && (
        <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm overflow-hidden" data-testid="kpi-diagnostics-backups">
          <div className="p-5 border-b border-brand-primary/10 flex items-center gap-3">
            <HardDrive className="h-5 w-5 text-terracotta" strokeWidth={1.7} />
            <div>
              <p className="font-heading text-lg text-brand-primary">Recent backup history</p>
              <p className="text-xs text-brand-primary/55 mt-0.5">
                {report.backup_history.most_recent_success_at
                  ? <>Most recent successful backup: <span className="font-medium text-brand-primary">{new Date(report.backup_history.most_recent_success_at).toLocaleString()}</span></>
                  : "No successful backups recorded — recovery from Drive snapshot not possible."}
              </p>
            </div>
          </div>
          {report.backup_history.runs?.length === 0 ? (
            <div className="p-5 text-sm text-brand-primary/55">No backup runs found in <span className="font-mono">backup_history</span>.</div>
          ) : (
            <table className="min-w-full">
              <thead className="bg-cream text-[10px] uppercase tracking-wider text-brand-primary/55">
                <tr>
                  <th className="text-left  px-5 py-2.5">When</th>
                  <th className="text-left  px-5 py-2.5">Status</th>
                  <th className="text-left  px-5 py-2.5">Filename</th>
                  <th className="text-right px-5 py-2.5">Size</th>
                  <th className="text-left  px-5 py-2.5">Error</th>
                </tr>
              </thead>
              <tbody>
                {report.backup_history.runs.map((r, i) => (
                  <tr key={i} className="border-t border-brand-primary/5">
                    <td className="px-5 py-2.5 text-sm">{r.at ? new Date(r.at).toLocaleString() : "—"}</td>
                    <td className="px-5 py-2.5">
                      <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${r.ok ? "bg-sage/15 text-sage" : "bg-terracotta/10 text-terracotta"}`}>
                        {r.ok ? "ok" : "failed"}
                      </Badge>
                    </td>
                    <td className="px-5 py-2.5 text-sm font-mono text-brand-primary/75 truncate max-w-xs">{r.filename || "—"}</td>
                    <td className="px-5 py-2.5 text-right font-mono text-sm">{r.size_bytes ? formatBytes(r.size_bytes) : "—"}</td>
                    <td className="px-5 py-2.5 text-sm text-terracotta truncate max-w-sm">{r.error || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* ── deep trace (RCA) ───────────────────────────────────── */}
      {report && (
        <DeepTracePanel />
      )}

      {/* ── read-path comparison (which path is hiding the row?) ─ */}
      {report && (
        <ReadPathComparePanel />
      )}

      {/* ── targeted week recovery ─────────────────────────────── */}
      {report && (
        <WeekRecoveryPanel />
      )}

      {/* ── audit search ───────────────────────────────────────── */}
      {report && (
        <AuditSearchPanel />
      )}

      {/* footer */}
      <p className="text-xs text-brand-primary/40 text-center pt-2">
        Endpoint: <span className="font-mono">GET /api/admin/kpi-diagnostics</span> — read-only, no mutations performed.
      </p>
    </div>
  );
}

function StatTile({ label, value, subline, sublineTone = "muted", icon: Icon, testId }) {
  return (
    <Card data-testid={testId} className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5">
      <div className="flex items-center gap-2.5 mb-3">
        {Icon && <Icon className="h-4 w-4 text-terracotta" strokeWidth={1.7} />}
        <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">{label}</p>
      </div>
      <p className="font-heading text-3xl font-light text-brand-primary leading-none">{value ?? "—"}</p>
      {subline && (
        <p className={`text-xs mt-2 ${sublineTone === "warn" ? "text-terracotta" : sublineTone === "ok" ? "text-sage" : "text-brand-primary/55"}`}>
          {subline}
        </p>
      )}
    </Card>
  );
}

function formatBytes(b) {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
}


/**
 * ReadPathComparePanel — answers "is this (category, week) row visible
 * across all four read paths?" Runs them in parallel and surfaces a
 * row-count matrix so an admin can tell at a glance which path (if any)
 * is hiding the row.
 *
 * Calls GET /api/admin/kpi-weeks/read-path-compare. Read-only.
 */
function ReadPathComparePanel() {
  const [category, setCategory] = useState("attendance");
  const [week, setWeek] = useState("2026-06-14");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const { data: resp } = await api.get(
        `/admin/kpi-weeks/read-path-compare?category=${encodeURIComponent(category)}&week=${encodeURIComponent(week)}`
      );
      setData(resp);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 space-y-4" data-testid="read-path-compare-panel">
      <div>
        <p className="font-heading text-lg text-brand-primary">Read-path comparison</p>
        <p className="text-xs text-brand-primary/55 mt-0.5">
          For one (category, week), shows what each of the four read paths sees: direct Atlas query, <span className="font-mono">/api/kpi-entries</span>, <span className="font-mono">/api/kpi-summary</span>, and the diagnostic per-category roll-up. Any disagreement is flagged.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Category</label>
          <input value={category} onChange={(e) => setCategory(e.target.value)}
                 className="mt-1 w-full rounded-xl border border-brand-primary/20 bg-cream/40 px-3 py-2 font-mono text-sm"
                 placeholder="attendance"
                 data-testid="read-path-compare-category" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Week (YYYY-MM-DD)</label>
          <input value={week} onChange={(e) => setWeek(e.target.value)}
                 className="mt-1 w-full rounded-xl border border-brand-primary/20 bg-cream/40 px-3 py-2 font-mono text-sm"
                 placeholder="2026-06-14"
                 data-testid="read-path-compare-week" />
        </div>
        <div className="flex items-end">
          <Button onClick={run} disabled={loading} data-testid="read-path-compare-run"
                  className="rounded-full bg-brand-primary text-white hover:bg-brand-primary/90 w-full md:w-auto">
            {loading ? "Comparing…" : "Compare read paths"}
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-xl bg-terracotta/5 border border-terracotta/30 p-3 text-sm text-terracotta">{error}</div>
      )}

      {data && (
        <div className="space-y-4 pt-2 border-t border-brand-primary/10" data-testid="read-path-compare-results">
          <div className="flex items-center gap-3 flex-wrap">
            <Badge className={`rounded-full border-0 text-[10px] uppercase tracking-wider ${data.verdict === "agreement" ? "bg-sage/15 text-sage" : "bg-terracotta/15 text-terracotta"}`}>
              {data.verdict.replace("_", " ")}
            </Badge>
            <p className="text-sm text-brand-primary/65 font-mono">{data.category} @ {data.week}</p>
          </div>

          {/* Matrix */}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm">
              <thead className="bg-cream text-[10px] uppercase tracking-wider text-brand-primary/55">
                <tr>
                  <th className="text-left px-3 py-2">Read path</th>
                  <th className="text-left px-3 py-2">Sees this row?</th>
                  <th className="text-left px-3 py-2">Details</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-t border-brand-primary/5">
                  <td className="px-3 py-2 font-medium">Atlas direct find()</td>
                  <td className="px-3 py-2">
                    <Badge className={`rounded-full border-0 text-[10px] ${data.atlas_raw.row_count > 0 ? "bg-sage/15 text-sage" : "bg-terracotta/15 text-terracotta"}`}>
                      {data.atlas_raw.row_count > 0 ? `yes (${data.atlas_raw.row_count})` : "no"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-brand-primary/70">
                    {data.atlas_raw.rows.map((r, i) =>
                      <div key={i}>{r.id} value={r.value} source={r.source}</div>
                    )}
                  </td>
                </tr>
                <tr className="border-t border-brand-primary/5">
                  <td className="px-3 py-2 font-medium">/api/kpi-entries (post-Pydantic)</td>
                  <td className="px-3 py-2">
                    <Badge className={`rounded-full border-0 text-[10px] ${data.kpi_entries_api.row_count > 0 ? "bg-sage/15 text-sage" : "bg-terracotta/15 text-terracotta"}`}>
                      {data.kpi_entries_api.row_count > 0 ? `yes (${data.kpi_entries_api.row_count})` : "no"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-brand-primary/70">
                    {data.kpi_entries_api.endpoint_truncated_at_5000 && (
                      <span className="text-terracotta">⚠️ truncated at 5000 rows — </span>
                    )}
                    scanned {data.kpi_entries_api.total_rows_scanned} total rows
                  </td>
                </tr>
                <tr className="border-t border-brand-primary/5">
                  <td className="px-3 py-2 font-medium">/api/kpi-summary (dashboard)</td>
                  <td className="px-3 py-2">
                    <Badge className={`rounded-full border-0 text-[10px] ${data.kpi_summary_card.ytd_includes_requested_week ? "bg-sage/15 text-sage" : "bg-terracotta/15 text-terracotta"}`}>
                      {data.kpi_summary_card.ytd_includes_requested_week ? "yes (in YTD bucket)" : "no (not in YTD bucket)"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-brand-primary/70 space-y-0.5">
                    <div>this_week_value: <span className="text-brand-primary">{String(data.kpi_summary_card.this_week_value)}</span></div>
                    <div>last_week_value: <span className="text-brand-primary">{String(data.kpi_summary_card.last_week_value)}</span></div>
                    <div>primary (YTD avg): <span className="text-brand-primary">{String(data.kpi_summary_card.primary_value_ytd_average)}</span></div>
                    <div>requested week is current: {String(data.kpi_summary_card.requested_week_is_current_week)}</div>
                    <div>requested week is latest YTD: {String(data.kpi_summary_card.requested_week_is_latest_ytd_row)}</div>
                  </td>
                </tr>
                <tr className="border-t border-brand-primary/5">
                  <td className="px-3 py-2 font-medium">/api/admin/kpi-diagnostics per_category</td>
                  <td className="px-3 py-2">
                    <Badge className={`rounded-full border-0 text-[10px] ${data.diagnostics_per_category.requested_week_present_in_distinct ? "bg-sage/15 text-sage" : "bg-terracotta/15 text-terracotta"}`}>
                      {data.diagnostics_per_category.requested_week_present_in_distinct ? "yes" : "no"}
                    </Badge>
                  </td>
                  <td className="px-3 py-2 font-mono text-xs text-brand-primary/70 space-y-0.5">
                    <div>category count: <span className="text-brand-primary">{data.diagnostics_per_category.count}</span></div>
                    <div>distinct weeks: <span className="text-brand-primary">{data.diagnostics_per_category.distinct_weeks}</span></div>
                    <div>earliest week: <span className="text-brand-primary">{data.diagnostics_per_category.earliest_week}</span></div>
                    <div>latest week: <span className="text-brand-primary">{data.diagnostics_per_category.latest_week}</span></div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          {data.mismatch_flags?.length > 0 && (
            <div className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-3 space-y-1" data-testid="read-path-compare-flags">
              <p className="text-sm font-semibold text-terracotta">Mismatch flags</p>
              <ul className="text-xs text-brand-primary/80 space-y-1 list-disc pl-5">
                {data.mismatch_flags.map((f, i) => <li key={i}>{f}</li>)}
              </ul>
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer text-brand-primary/55">Show raw windows + values</summary>
            <pre className="mt-2 max-h-72 overflow-auto bg-cream/50 p-3 rounded-md text-[10px] text-brand-primary/70">
              {JSON.stringify(data.kpi_summary_card.windows_used, null, 2)}
            </pre>
          </details>
        </div>
      )}
    </Card>
  );
}


/**
 * DeepTracePanel — RCA tool for "where did this row go?" questions.
 *
 * Calls GET /api/admin/kpi-weeks/deep-trace. For each requested week we get
 * back: every audit event (via 4-channel union), per-entity_id lifecycle,
 * the current live state of each id, per-category roll-up, and anomaly
 * detection (rekeyed / duplicates / soft-deleted / unexpected category or
 * source).
 *
 * Read-only. The "Copy JSON" button bundles the full response so an admin
 * can paste it into a support thread without screenshotting 50 events.
 */
function DeepTracePanel() {
  const [weeks, setWeeks] = useState("2026-05-31,2026-06-07,2026-06-14");
  const [categories, setCategories] = useState("attendance,fc_kids_attendance,online_audience");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const params = new URLSearchParams();
      params.set("weeks", weeks);
      if (categories.trim()) params.set("categories", categories.trim());
      const { data: resp } = await api.get(`/admin/kpi-weeks/deep-trace?${params.toString()}`);
      setData(resp);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  const copyJson = async () => {
    if (!data) return;
    try {
      await navigator.clipboard.writeText(JSON.stringify(data, null, 2));
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch {
      // Fall back to a textarea selection if clipboard API is blocked.
      const ta = document.createElement("textarea");
      ta.value = JSON.stringify(data, null, 2);
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); } catch { /* ignore */ }
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    }
  };

  return (
    <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 space-y-4" data-testid="deep-trace-panel">
      <div>
        <p className="font-heading text-lg text-brand-primary">Deep trace — full lifecycle &amp; anomalies</p>
        <p className="text-xs text-brand-primary/55 mt-0.5">
          For each requested week we union four audit channels (metadata, before, after, summary), follow every entity_id to its current row, and surface anomalies (rekeyed weeks, duplicate rows, soft-deletes, unexpected category/source). Read-only.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Weeks (comma-separated, YYYY-MM-DD)</label>
          <input
            value={weeks}
            onChange={(e) => setWeeks(e.target.value)}
            className="mt-1 w-full rounded-xl border border-brand-primary/20 bg-cream/40 px-3 py-2 font-mono text-sm"
            placeholder="2026-05-31,2026-06-07"
            data-testid="deep-trace-weeks"
          />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Categories (optional)</label>
          <input
            value={categories}
            onChange={(e) => setCategories(e.target.value)}
            className="mt-1 w-full rounded-xl border border-brand-primary/20 bg-cream/40 px-3 py-2 font-mono text-sm"
            placeholder="attendance,fc_kids_attendance,online_audience"
            data-testid="deep-trace-categories"
          />
        </div>
      </div>

      <div className="flex justify-end gap-2">
        {data && (
          <Button
            onClick={copyJson}
            variant="outline"
            className="rounded-full border-brand-primary/20"
            data-testid="deep-trace-copy-json"
          >
            {copied ? "Copied!" : "Copy JSON"}
          </Button>
        )}
        <Button
          onClick={run}
          disabled={loading}
          data-testid="deep-trace-run"
          className="rounded-full bg-brand-primary text-white hover:bg-brand-primary/90"
        >
          {loading ? "Tracing…" : "Run deep trace"}
        </Button>
      </div>

      {error && (
        <div className="rounded-xl bg-terracotta/5 border border-terracotta/30 p-3 text-sm text-terracotta">{error}</div>
      )}

      {data && (
        <div className="space-y-5 pt-2 border-t border-brand-primary/10" data-testid="deep-trace-results">
          {Object.entries(data.per_week || {}).map(([week, payload]) => (
            <DeepTraceWeekBlock key={week} week={week} payload={payload} />
          ))}
        </div>
      )}
    </Card>
  );
}


function DeepTraceWeekBlock({ week, payload }) {
  const lc = payload.lifecycle_by_entity_id || [];
  const anomalies = payload.anomalies || {};
  const anomaliesCount =
    (anomalies.rekeyed_rows?.length || 0) +
    (anomalies.duplicate_rows?.length || 0) +
    (anomalies.soft_deleted_rows?.length || 0) +
    (anomalies.unexpected_categories?.length || 0) +
    (anomalies.unexpected_sources?.length || 0);

  return (
    <div className="rounded-xl border border-brand-primary/10 bg-cream/30 p-4 space-y-3" data-testid={`deep-trace-week-${week}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <p className="font-mono text-sm font-semibold text-brand-primary">{week}</p>
        <Badge className="rounded-full bg-brand-primary/8 text-brand-primary/75 border-0 text-[10px]">
          {payload.live_row_count} live rows
        </Badge>
        <Badge className="rounded-full bg-brand-primary/8 text-brand-primary/75 border-0 text-[10px]">
          {payload.audit_events_total} audit events
        </Badge>
        <Badge className="rounded-full bg-brand-primary/8 text-brand-primary/75 border-0 text-[10px]">
          {lc.length} entity_ids touched
        </Badge>
        <Badge
          className={`rounded-full border-0 text-[10px] ${
            anomaliesCount > 0 ? "bg-terracotta/15 text-terracotta" : "bg-sage/15 text-sage"
          }`}
        >
          {anomaliesCount} anomalies
        </Badge>
      </div>

      {/* Per-category lifecycle */}
      {Object.entries(payload.per_category || {}).map(([cat, cinfo]) => (
        <details key={cat} className="rounded-lg bg-white border border-brand-primary/10 px-3 py-2">
          <summary className="cursor-pointer text-sm text-brand-primary flex items-center gap-2 flex-wrap">
            <span className="font-mono font-semibold">{cat}</span>
            <Badge className="rounded-full bg-brand-primary/5 text-brand-primary/65 border-0 text-[10px]">
              live: {cinfo.live_row_count}
            </Badge>
            <Badge className="rounded-full bg-brand-primary/5 text-brand-primary/65 border-0 text-[10px]">
              events: {cinfo.audit_event_count}
            </Badge>
          </summary>
          {cinfo.live_rows?.length > 0 && (
            <div className="mt-2">
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Live rows</p>
              <table className="min-w-full text-xs mt-1">
                <thead className="text-[10px] uppercase tracking-wider text-brand-primary/45">
                  <tr>
                    <th className="text-left px-2 py-1">id</th>
                    <th className="text-right px-2 py-1">value</th>
                    <th className="text-left px-2 py-1">source</th>
                    <th className="text-left px-2 py-1">created_by</th>
                    <th className="text-left px-2 py-1">created_at</th>
                  </tr>
                </thead>
                <tbody>
                  {cinfo.live_rows.map((r) => (
                    <tr key={r.id} className="border-t border-brand-primary/5">
                      <td className="px-2 py-1 font-mono">{r.id}</td>
                      <td className="px-2 py-1 text-right font-mono">{r.value}</td>
                      <td className="px-2 py-1">{r.source}</td>
                      <td className="px-2 py-1">{r.created_by}</td>
                      <td className="px-2 py-1 font-mono text-[10px]">{(r.created_at || "").slice(0, 19).replace("T", " ")}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {cinfo.audit_events?.length > 0 && (
            <div className="mt-3">
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Audit timeline</p>
              <div className="mt-1 max-h-56 overflow-y-auto rounded-md bg-cream/50 p-2 space-y-1">
                {cinfo.audit_events.map((e, i) => (
                  <div key={i} className="flex gap-2 items-start text-[11px] flex-wrap">
                    <span className="text-brand-primary/45 font-mono shrink-0">{(e.at || "").slice(0, 19).replace("T", " ")}</span>
                    <Badge className="rounded-full bg-brand-primary/8 text-brand-primary/70 border-0 text-[10px] shrink-0">{e.action}</Badge>
                    <span className="font-mono text-brand-primary/55 shrink-0">{e.entity_id}</span>
                    <span className="text-brand-primary/70 shrink-0">{e.actor_email}</span>
                    <span className="text-brand-primary/85">{e.summary}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </details>
      ))}

      {/* Entity-id lifecycle */}
      {lc.length > 0 && (
        <details className="rounded-lg bg-white border border-brand-primary/10 px-3 py-2">
          <summary className="cursor-pointer text-sm text-brand-primary">
            All entity_ids touched ({lc.length}) — final status per row
          </summary>
          <table className="min-w-full text-xs mt-2">
            <thead className="text-[10px] uppercase tracking-wider text-brand-primary/45">
              <tr>
                <th className="text-left px-2 py-1">entity_id</th>
                <th className="text-left px-2 py-1">categories</th>
                <th className="text-left px-2 py-1">weeks seen</th>
                <th className="text-left px-2 py-1">values</th>
                <th className="text-left px-2 py-1">final status</th>
              </tr>
            </thead>
            <tbody>
              {lc.map((s) => (
                <tr key={s.entity_id} className="border-t border-brand-primary/5">
                  <td className="px-2 py-1 font-mono">{s.entity_id}</td>
                  <td className="px-2 py-1 font-mono">{s.categories_seen?.join(", ")}</td>
                  <td className="px-2 py-1 font-mono">{s.weeks_seen?.join(", ")}</td>
                  <td className="px-2 py-1 font-mono">{s.values_seen?.join(", ")}</td>
                  <td className="px-2 py-1">
                    <span className={
                      s.final_status === "exists_as_expected" ? "text-sage" :
                      s.final_status?.startsWith("exists_but_week_now") ? "text-gold" :
                      "text-terracotta"
                    }>
                      {s.final_status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {/* Anomalies */}
      {anomaliesCount > 0 && (
        <div className="rounded-lg border border-terracotta/30 bg-terracotta/5 p-3 space-y-2" data-testid={`deep-trace-anomalies-${week}`}>
          <p className="text-sm font-semibold text-terracotta">Anomalies detected</p>
          {anomalies.rekeyed_rows?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Re-keyed rows ({anomalies.rekeyed_rows.length})</p>
              <ul className="text-xs font-mono text-brand-primary/80 mt-1 space-y-0.5">
                {anomalies.rekeyed_rows.map((r, i) => (
                  <li key={i}>{r.entity_id}: audited as {r.audited_week}, now lives at {r.current_week} ({r.category} = {r.value}, source={r.source})</li>
                ))}
              </ul>
            </div>
          )}
          {anomalies.duplicate_rows?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Duplicates ({anomalies.duplicate_rows.length})</p>
              <ul className="text-xs font-mono text-brand-primary/80 mt-1 space-y-0.5">
                {anomalies.duplicate_rows.map((d, i) => (
                  <li key={i}>{d.category} @ {d.week_of} → {d.count} rows: {d.ids.join(", ")}</li>
                ))}
              </ul>
            </div>
          )}
          {anomalies.soft_deleted_rows?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Soft-deleted ({anomalies.soft_deleted_rows.length})</p>
              <pre className="text-[10px] text-brand-primary/70 overflow-x-auto">{JSON.stringify(anomalies.soft_deleted_rows, null, 2)}</pre>
            </div>
          )}
          {anomalies.unexpected_categories?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Unexpected categories ({anomalies.unexpected_categories.length})</p>
              <pre className="text-[10px] text-brand-primary/70 overflow-x-auto">{JSON.stringify(anomalies.unexpected_categories, null, 2)}</pre>
            </div>
          )}
          {anomalies.unexpected_sources?.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Unexpected sources ({anomalies.unexpected_sources.length})</p>
              <pre className="text-[10px] text-brand-primary/70 overflow-x-auto">{JSON.stringify(anomalies.unexpected_sources, null, 2)}</pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}


/**
 * AuditSearchPanel — finds KPI mutations matching a date window + categories.
 * Designed to answer "did someone bulk-delete attendance categories in a
 * specific time window?" by aggregating audit_log entries grouped by
 * (action, actor) and by category.
 *
 * Read-only. Calls GET /api/admin/kpi-weeks/audit-search.
 */
function AuditSearchPanel() {
  const [since, setSince] = useState("2026-05-25");
  const [until, setUntil] = useState("");
  const [categories, setCategories] = useState("attendance,fc_kids_attendance,online_audience");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const run = async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (since) params.set("since", since);
      if (until) params.set("until", until);
      if (categories.trim()) params.set("categories", categories.trim());
      const { data } = await api.get(`/admin/kpi-weeks/audit-search?${params.toString()}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 space-y-4" data-testid="audit-search-panel">
      <div>
        <p className="font-heading text-lg text-brand-primary">Audit-log search (root cause)</p>
        <p className="text-xs text-brand-primary/55 mt-0.5">
          Search KPI mutations in a time window. Answers "who deleted/edited what, when".
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Since</label>
          <input value={since} onChange={(e) => setSince(e.target.value)}
                 className="mt-1 w-full rounded-xl border border-brand-primary/20 bg-cream/40 px-3 py-2 font-mono text-sm"
                 placeholder="YYYY-MM-DD" data-testid="audit-search-since" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Until (optional)</label>
          <input value={until} onChange={(e) => setUntil(e.target.value)}
                 className="mt-1 w-full rounded-xl border border-brand-primary/20 bg-cream/40 px-3 py-2 font-mono text-sm"
                 placeholder="YYYY-MM-DD" data-testid="audit-search-until" />
        </div>
        <div className="md:col-span-2">
          <label className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">Categories (comma-separated)</label>
          <input value={categories} onChange={(e) => setCategories(e.target.value)}
                 className="mt-1 w-full rounded-xl border border-brand-primary/20 bg-cream/40 px-3 py-2 font-mono text-sm"
                 data-testid="audit-search-categories" />
        </div>
      </div>
      <div className="flex justify-end">
        <Button onClick={run} disabled={loading} data-testid="audit-search-run"
                className="rounded-full bg-brand-primary text-white hover:bg-brand-primary/90">
          {loading ? "Searching…" : "Search"}
        </Button>
      </div>

      {error && (
        <div className="rounded-xl bg-terracotta/5 border border-terracotta/30 p-3 text-sm text-terracotta">{error}</div>
      )}

      {data && (
        <div className="space-y-4 pt-2 border-t border-brand-primary/10" data-testid="audit-search-results">
          <p className="text-sm text-brand-primary">
            <span className="font-semibold">{data.total_matches}</span> matching audit entries
            {data.earliest && (
              <> from <span className="font-mono">{data.earliest?.slice(0, 19).replace("T", " ")}</span> to <span className="font-mono">{data.latest?.slice(0, 19).replace("T", " ")}</span></>
            )}
          </p>

          {data.by_action_actor.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold mb-2">By action × actor</p>
              <table className="min-w-full text-sm">
                <thead className="bg-cream text-[10px] uppercase tracking-wider text-brand-primary/55">
                  <tr><th className="text-left px-3 py-1.5">Action</th><th className="text-left px-3 py-1.5">Actor</th><th className="text-right px-3 py-1.5">Count</th></tr>
                </thead>
                <tbody>
                  {data.by_action_actor.map((r, i) => (
                    <tr key={i} className="border-t border-brand-primary/5">
                      <td className="px-3 py-1.5 font-mono">{r.action}</td>
                      <td className="px-3 py-1.5">{r.actor_email}</td>
                      <td className="px-3 py-1.5 text-right font-mono font-semibold">{r.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {data.by_category.length > 0 && (
            <div>
              <p className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold mb-2">By category</p>
              <div className="flex flex-wrap gap-2">
                {data.by_category.map((c) => (
                  <Badge key={c.category} className="rounded-full bg-brand-primary/8 text-brand-primary/80 border-0">
                    {c.category} × {c.count}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          <details className="text-xs">
            <summary className="cursor-pointer text-brand-primary/65 font-medium">Raw entries ({data.entries.length})</summary>
            <div className="mt-2 max-h-96 overflow-y-auto rounded-lg bg-cream/50 p-3 space-y-1.5">
              {data.entries.map((e, i) => (
                <div key={i} className="flex gap-3 items-start">
                  <span className="text-brand-primary/45 font-mono shrink-0">{e.at?.slice(0, 19).replace("T", " ")}</span>
                  <Badge className="rounded-full bg-brand-primary/8 text-brand-primary/70 border-0 text-[10px] shrink-0">{e.action}</Badge>
                  <span className="text-brand-primary/70 shrink-0">{e.actor_email}</span>
                  <span className="text-brand-primary/85 truncate">{e.summary}</span>
                </div>
              ))}
            </div>
          </details>
        </div>
      )}
    </Card>
  );
}


/**
 * Targeted "specific weeks" recovery workflow.
 *
 *   1. Admin enters comma-separated YYYY-MM-DD week starts (defaults to the
 *      two weeks reported missing).
 *   2. Click "Inspect" → shows per-week status: rows currently in DB, audit
 *      log activity (with actor + action + summary), and a verdict.
 *   3. Click "Export current kpi_entries" → downloads a JSON safety-net
 *      file BEFORE any restore so admins can undo.
 *   4. Click "List backup snapshots" → lists Drive snapshots, newest first.
 *   5. Pick a snapshot → "Preview restore" shows which rows would be added
 *      and which would be skipped (id collision = preserved).
 *   6. Click "Restore now" → actually inserts the missing rows. Existing
 *      rows are NEVER overwritten. Audit-logged.
 *
 * Every step except the final Restore is fully read-only. The restore
 * button itself runs a dry-run first and requires explicit confirmation.
 */
function WeekRecoveryPanel() {
  const [weeks, setWeeks] = useState("2026-05-31,2026-06-07");
  const [inspect, setInspect] = useState(null);
  const [inspectLoading, setInspectLoading] = useState(false);
  const [snaps, setSnaps] = useState(null);
  const [snapsLoading, setSnapsLoading] = useState(false);
  const [selectedSnap, setSelectedSnap] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [error, setError] = useState(null);

  const runInspect = async () => {
    setInspectLoading(true);
    setError(null);
    try {
      const { data } = await api.get(`/admin/kpi-weeks/inspect?weeks=${encodeURIComponent(weeks)}`);
      setInspect(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setInspectLoading(false);
    }
  };

  const exportCurrent = async () => {
    setError(null);
    try {
      const { data } = await api.get("/admin/kpi-weeks/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `kpi_entries_current_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.json`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    }
  };

  const listSnaps = async () => {
    setSnapsLoading(true);
    setError(null);
    try {
      const { data } = await api.get("/admin/kpi-weeks/backups");
      setSnaps(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setSnapsLoading(false);
    }
  };

  const runPreview = async () => {
    if (!selectedSnap) return;
    setPreviewLoading(true);
    setError(null);
    setPreview(null);
    try {
      const { data } = await api.get(
        `/admin/kpi-weeks/backup-preview?drive_file_id=${encodeURIComponent(selectedSnap)}&weeks=${encodeURIComponent(weeks)}`
      );
      setPreview(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setPreviewLoading(false);
    }
  };

  const runRestore = async () => {
    if (!selectedSnap) return;
    const wouldRestore = preview?.would_restore_count ?? 0;
    if (wouldRestore === 0) return;
    const confirmed = window.confirm(
      `This will insert ${wouldRestore} rows from the snapshot for weeks ${weeks}.\n\n` +
      `Existing rows are NEVER overwritten. Continue?`
    );
    if (!confirmed) return;
    setRestoring(true);
    setError(null);
    setRestoreResult(null);
    try {
      const { data } = await api.post(
        `/admin/kpi-weeks/restore?dry_run=false&drive_file_id=${encodeURIComponent(selectedSnap)}&weeks=${encodeURIComponent(weeks)}`
      );
      setRestoreResult(data);
      // Re-run inspect so the page reflects the new state.
      runInspect();
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally {
      setRestoring(false);
    }
  };

  return (
    <Card className="rounded-2xl bg-white border border-brand-primary/10 shadow-warm p-5 space-y-5" data-testid="week-recovery-panel">
      <div>
        <p className="font-heading text-lg text-brand-primary">Targeted week recovery</p>
        <p className="text-xs text-brand-primary/55 mt-0.5">
          Inspect specific weeks, then optionally restore missing rows from a Drive snapshot. Existing rows are never overwritten.
        </p>
      </div>

      {/* Weeks input + actions */}
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex-1 min-w-[280px]">
          <label className="text-[11px] uppercase tracking-wider text-brand-primary/55 font-semibold">
            Weeks to inspect (comma-separated YYYY-MM-DD)
          </label>
          <input
            data-testid="week-recovery-input"
            value={weeks}
            onChange={(e) => setWeeks(e.target.value)}
            className="mt-1 w-full rounded-xl border border-brand-primary/20 bg-cream/40 px-3 py-2 font-mono text-sm"
            placeholder="2026-05-31,2026-06-07"
          />
        </div>
        <Button
          onClick={runInspect}
          disabled={inspectLoading || !weeks.trim()}
          data-testid="week-recovery-inspect-btn"
          className="rounded-full bg-brand-primary text-white hover:bg-brand-primary/90"
        >
          {inspectLoading ? "Inspecting…" : "Inspect"}
        </Button>
        <Button
          onClick={exportCurrent}
          variant="outline"
          data-testid="week-recovery-export-btn"
          className="rounded-full border-brand-primary/20"
        >
          Export current
        </Button>
      </div>

      {error && (
        <div className="rounded-xl bg-terracotta/5 border border-terracotta/30 p-3 text-sm text-terracotta">{error}</div>
      )}

      {/* Inspect results */}
      {inspect && Object.entries(inspect.per_week).map(([w, info]) => {
        const verdictTone = info.verdict === "present"
          ? "bg-sage/15 text-sage"
          : info.verdict === "audit_says_existed"
          ? "bg-terracotta/15 text-terracotta"
          : "bg-gold/15 text-gold";
        return (
          <div key={w} className="rounded-xl border border-brand-primary/10 p-4" data-testid={`inspect-week-${w}`}>
            <div className="flex items-center gap-3 mb-3">
              <p className="font-mono font-semibold text-brand-primary">{w}</p>
              <Badge className={`rounded-full text-[10px] uppercase tracking-wider ${verdictTone}`}>
                {info.verdict.replaceAll("_", " ")}
              </Badge>
            </div>
            <p className="text-sm text-brand-primary/75">
              In DB now: <span className="font-mono font-semibold">{info.in_db_now.row_count}</span> rows
              ({info.in_db_now.categories_with_data.join(", ") || "—"})
            </p>
            {info.audit_history.count > 0 && (
              <details className="mt-2 text-sm">
                <summary className="cursor-pointer text-brand-primary/65">
                  Audit log mentions this week: <span className="font-semibold">{info.audit_history.count}</span> entries
                </summary>
                <div className="mt-2 max-h-64 overflow-y-auto rounded-lg bg-cream/50 p-3 text-xs space-y-1.5">
                  {info.audit_history.entries.map((a, i) => (
                    <div key={i} className="flex gap-3 items-start">
                      <span className="text-brand-primary/45 font-mono shrink-0">{a.at?.slice(0, 19).replace("T", " ")}</span>
                      <Badge className="rounded-full bg-brand-primary/8 text-brand-primary/70 border-0 text-[10px] shrink-0">{a.action}</Badge>
                      <span className="text-brand-primary/70">{a.actor_email}</span>
                      <span className="text-brand-primary/85">{a.summary}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </div>
        );
      })}

      {/* Snapshots */}
      <div className="border-t border-brand-primary/10 pt-5">
        <div className="flex items-center justify-between mb-2">
          <p className="font-medium text-brand-primary">Drive backup snapshots</p>
          <Button
            onClick={listSnaps}
            disabled={snapsLoading}
            variant="outline"
            data-testid="week-recovery-list-snaps-btn"
            className="rounded-full border-brand-primary/20"
            size="sm"
          >
            {snapsLoading ? "Loading…" : (snaps ? "Refresh" : "List snapshots")}
          </Button>
        </div>
        {snaps && snaps.backups.length === 0 && (
          <p className="text-sm text-brand-primary/55">No snapshots found in Drive Backups/ folder.</p>
        )}
        {snaps && snaps.backups.length > 0 && (
          <div className="space-y-1.5 max-h-64 overflow-y-auto">
            {snaps.backups.map((s) => (
              <label key={s.drive_file_id} className="flex items-center gap-3 p-2.5 rounded-lg hover:bg-cream cursor-pointer">
                <input
                  type="radio"
                  name="snap"
                  value={s.drive_file_id}
                  checked={selectedSnap === s.drive_file_id}
                  onChange={() => { setSelectedSnap(s.drive_file_id); setPreview(null); setRestoreResult(null); }}
                  data-testid={`snap-radio-${s.date_label}`}
                />
                <span className="font-mono text-sm text-brand-primary">{s.date_label}</span>
                <span className="text-xs text-brand-primary/60 truncate flex-1">{s.filename}</span>
                <span className="text-xs text-brand-primary/55">{formatBytes(s.size_bytes)}</span>
              </label>
            ))}
          </div>
        )}
        {selectedSnap && (
          <div className="mt-3 flex gap-2">
            <Button
              onClick={runPreview}
              disabled={previewLoading}
              variant="outline"
              data-testid="week-recovery-preview-btn"
              className="rounded-full border-brand-primary/20"
              size="sm"
            >
              {previewLoading ? "Previewing…" : "Preview restore"}
            </Button>
            <Button
              onClick={runRestore}
              disabled={restoring || !preview || (preview?.would_restore_count ?? 0) === 0}
              data-testid="week-recovery-restore-btn"
              className="rounded-full bg-terracotta text-white hover:bg-terracotta-hover"
              size="sm"
            >
              {restoring ? "Restoring…" : `Restore now (${preview?.would_restore_count ?? "?"} rows)`}
            </Button>
          </div>
        )}
        {preview && (
          <div className="mt-3 rounded-xl bg-cream/60 p-3 text-sm" data-testid="restore-preview-result">
            <p className="text-brand-primary"><span className="font-semibold">{preview.would_restore_count}</span> rows would be inserted, <span className="font-semibold">{preview.already_present_count}</span> already present (will be skipped).</p>
            {preview.classification.would_restore.length > 0 && (
              <details className="mt-2 text-xs">
                <summary className="cursor-pointer text-brand-primary/65">Show rows to restore</summary>
                <ul className="mt-2 space-y-1 font-mono">
                  {preview.classification.would_restore.map((r) => (
                    <li key={r.id} className="text-brand-primary/75">
                      {r.week_of} · {r.category} = {r.value} <span className="text-brand-primary/40">({r.source}, by {r.created_by})</span>
                    </li>
                  ))}
                </ul>
              </details>
            )}
          </div>
        )}
        {restoreResult && (
          <div className="mt-3 rounded-xl bg-sage/8 border border-sage/30 p-3 text-sm text-brand-primary" data-testid="restore-result">
            <p className="font-semibold text-sage">✓ Restore complete</p>
            <p>Inserted {restoreResult.restored_count} rows. Skipped {restoreResult.skipped_existing_count} pre-existing rows.</p>
            <p className="text-xs text-brand-primary/55 mt-1">Audit logged: {restoreResult.audit_recorded ? "yes" : "no (non-fatal)"}</p>
          </div>
        )}
      </div>
    </Card>
  );
}
